// Определение медленного соединения — общая логика для всех SPA репозитория
// (Creative Hub, Asset Lab). Здесь только измерение: иконку в шапке рисует общий
// general/components/SlowNetworkBadge, а тост каждое приложение показывает своими
// средствами через колбэк — это единственное, что у сервисов различается
// (Creative Hub на react-notification-system-redux, Asset Lab на нотификациях
// кита) (MYUA-16416).

// Один текст на всплывающее предупреждение и на тултип индикатора в шапке.
export const SLOW_NETWORK_MESSAGE =
  'Your connection is currently unstable. Please check your network.';

// Автозакрытие тоста, секунды: дальше о проблеме напоминает иконка wifi в шапке.
export const SLOW_NETWORK_WARNING_DISMISS_S = 7;

const SLOW_NETWORK_THRESHOLD_MS = 10000;
// Пока сеть медленная, перепроверяем её с этим интервалом; когда очередная
// проверка проходит быстро, индикатор в шапке скрывается.
const SLOW_NETWORK_RECHECK_MS = 30000;

// Порог задержки по Network Information API. Одного `effectiveType` мало: браузер
// округляет соединение до поколения сети и на канале 1.5 Мбит/с спокойно отдаёт
// '4g' (MYUA-16590). `rtt` — сырая оценка HTTP-задержки по недавним запросам,
// огрублённая до 25 мс; на нормальном канале это 50–150 мс, так что 400 мс уже
// означает заметно плохую связь.
//
// Соседнее поле `connection.downlink` намеренно НЕ используется, хотя оно и ловит
// троттлинг DevTools: это медленно затухающая оценка полосы по недавно
// наблюдённому трафику, общая на весь браузер. На здоровом соединении она
// спокойно держится около 1.5 Мбит/с (замерено), то есть порог «меньше 2 Мбит/с»
// давал бы предупреждение почти всем — и в Creative Hub тоже.
const SLOW_NETWORK_RTT_MS = 400;
// Скачивание крошечного файла (~0.4 КБ) — это по сути замер задержки: столько
// времени уходит на установку соединения и первый байт. Для 0.4 КБ даже 800 мс
// означает, что связь плохая.
const SLOW_NETWORK_BENCH_MS = 800;

// Checks the CURRENT state of the connection (unlike the initial bundle-load
// benchmark, this can be re-run at any time).
export async function isNetworkSlowNow(): Promise<boolean> {
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  // 1. Check navigator API if supported (Chrome-only; в Firefox и Safari его нет,
  //    там сеть оценивает только замер ниже)
  if (connection) {
    const { effectiveType, rtt } = connection;
    const reason = ['2g', '3g'].includes(effectiveType)
      ? `effectiveType ${effectiveType}`
      : rtt > 0 && rtt > SLOW_NETWORK_RTT_MS
        ? `rtt ${rtt} ms`
        : '';

    if (reason) {
      console.warn(`Network speed benchmark by navigator API: ${reason}`);
      return true;
    }
  }

  // 2. Performance benchmark: fetch a small asset with cache buster
  // This helps when bundle was cached, but network is currently slow
  try {
    const benchStart = performance.now();
    // Fetching favicon as a small reliable asset from u-static
    await fetch(`/u-static/common/favicons/aa/favicon.svg?_v=${Date.now()}`, {
      cache: 'no-store',
    });
    const benchDuration = performance.now() - benchStart;

    if (benchDuration > SLOW_NETWORK_BENCH_MS) {
      console.warn(`Network speed benchmark by favicon: ${Math.round(benchDuration)} ms`);
      return true;
    }
  } catch (e) {
    // Ignore fetch errors to not disturb normal flow
  }

  return false;
}

/**
 * Замеряет скорость соединения на старте приложения и, если она плохая, зовёт
 * `onSlowDetected` (однократно), а затем перепроверяет сеть в фоне и зовёт
 * `onRestored`, как только связь восстановилась. На хорошей сети не делает ничего.
 *
 * Вызывать один раз за загрузку страницы: замер времени загрузки бандла имеет
 * смысл только на старте.
 */
export async function watchNetworkSpeed({
  onSlowDetected,
  onRestored,
}: {
  onSlowDetected: () => void;
  onRestored: () => void;
}): Promise<void> {
  const startTime = (window as any).APP_START_TIME || -1;

  if (startTime < 0) {
    console.warn('Network speed benchmark by bundle loading skipped: start time not available');
  }

  const loadDuration = performance.now() - startTime;

  // Check initial bundle load duration (from page start to JS execution)
  const slowInitialLoad = startTime > 0 && loadDuration > SLOW_NETWORK_THRESHOLD_MS;
  if (slowInitialLoad) {
    console.warn(`Network speed benchmark by initial bundle load: ${Math.round(loadDuration)} ms`);
  }

  if (!slowInitialLoad && !(await isNetworkSlowNow())) {
    return;
  }

  onSlowDetected();

  // Индикатор висит, пока перепроверки подтверждают проблему; как только сеть
  // восстановилась — скрываем его совсем.
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, SLOW_NETWORK_RECHECK_MS));
    if (!(await isNetworkSlowNow())) {
      onRestored();
      return;
    }
  }
}
